/**
 * Territory pass: one full-screen fragment shader draws ocean, land, fog, every nation's
 * fill pattern, borders, the push-focus front, fresh-capture glow, radiation ash, the
 * graticule and the outline of our sensor coverage (the union of every disc, edge-detected
 * from the sensor mask — one clean line instead of overlapping rings). Per-hex data comes
 * from small data textures (hex id lookup + hex state).
 */
import type { LoadedWorld } from '@sa/mapdata';
import { FULLSCREEN_VS, GLSL_COMMON, makeFullscreenVao, makeProgram, makeTexture, type GL, type Program } from './gl';
import { BORDER_ROUND, GLSL_HEX, STATE_W, WARP_AMP, WARP_RES, type HexLookup } from './hexfield';
import type { FrameParams } from './frame';

const FS = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
${GLSL_COMMON}
${GLSL_HEX}
uniform sampler2D uLand;      // land mask (linear, mipmapped)
uniform sampler2D uVis;       // screen-space sensor coverage: crisp (r), radar fringe (g), soft (b), crisp own-only (a)
uniform vec2 uWorld;          // world size, mu
uniform vec4 uOwnerCol[9];    // per owner id: rgb, pattern id
uniform int uMe;
uniform int uAllyMask;        // bit per player id
uniform float uTime;          // client seconds
uniform float uDpr;
uniform int uFront;           // focused front: 0 none, 1 advancing, 2 stalled, 3 losing
uniform int uHilite;          // a nation the Tab board points at (0 none): its land pulses
uniform float uHiliteK;       // that pulse, 0..1
uniform float uHiliteRed;     // 1: red (it just betrayed us), 0: the nation's colour
out vec4 outColor;

const vec3 C_ME = vec3(0.376, 1.0, 0.627);
const vec3 C_WHITE = vec3(0.84, 1.0, 0.894);
const vec3 C_GREY = vec3(0.588, 0.667, 0.627);
const vec3 C_ALLY = vec3(0.33, 0.84, 0.886);
const vec3 C_AMBER = vec3(1.0, 0.675, 0.204);

float bayer2(vec2 a) { a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }
float bayer4(vec2 a) { return bayer2(0.5 * a) * 0.25 + bayer2(a); }

/** Fill pattern mask (0/1) per nation pattern id, in dither-pixel space (see theme.ts). */
float pattern(int id, vec2 p) {
  float x = p.x, y = p.y;
  if (id == 0) return mod(y, 4.0) < 1.0 ? 1.0 : 0.0;                      // horizontal lines
  if (id == 1) return mod(x, 4.0) < 1.0 ? 1.0 : 0.0;                      // vertical lines
  if (id == 2) return (mod(x + y, 8.0) < 1.0 || mod(x - y, 8.0) < 1.0) ? 1.0 : 0.0; // cross-hatch
  if (id == 3) return mod(x - y, 6.0) < 1.0 ? 1.0 : 0.0;                  // anti-diagonal
  if (id == 4) return mod(x, 4.0) < 2.0 && mod(y, 4.0) < 2.0 ? 1.0 : 0.0; // coarse dots
  if (id == 5) return mod(y, 5.0) < 1.0 && mod(x, 8.0) < 5.0 ? 1.0 : 0.0; // dashes
  if (id == 6) return (mod(x, 6.0) < 1.0 || mod(y, 6.0) < 1.0) ? 1.0 : 0.0; // grid
  if (id == 7) return mod(x + y, 6.0) < 1.0 ? 1.0 : 0.0;                  // you: diagonal hatch
  if (id == 8) return mod(x, 3.0) < 1.0 && mod(y + 1.5 * floor(x / 3.0), 3.0) < 1.0 ? 1.0 : 0.0; // ally: staggered dots
  return bayer4(p) < 0.06 ? 1.0 : 0.0;                                    // neutral
}

bool isAlly(int o) { return o > 0 && ((uAllyMask >> o) & 1) == 1; }

/**
 * What a hex looks like, for corner rounding: never seen (−1), or its owner — foreign land
 * split by whether a sensor sees it now. Every visible edge (borders, the edge of what we have
 * seen, the edge of live sight) rounds the same way. CPU twin: the controller's pick class.
 */
int roundClass(uvec4 s) {
  if ((s.a & 2u) == 0u) return -1;
  int o = int(s.r);
  return o == uMe || (s.a & 1u) == 0u ? o : o + 16;
}

/** Polynomial smooth minimum (rounds the corner where two distance fields meet). */
float smin(float a, float b, float k) {
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}

/** Corner rounding between owners, in hex radii (BORDER_ROUND): inland borders read as curves, not a hex sawtooth. */
const float ROUND = ${BORDER_ROUND.toFixed(3)};

/**
 * One border between the pixel's owner class c0 (focus flag f0) and a neighbour's class ck (fk)
 * at edge distance d: accumulated per kind of border (smoothly, so hex corners stay rounded).
 */
void border(int c0, bool f0, int ck, bool fk, float d, float k,
            inout float dOwn, inout float dFront, inout float dAlly, inout float dEnemy, inout float dEE, inout float dRipple) {
  bool meA = c0 == uMe, meB = ck == uMe;
  if (meA || meB) {
    int other = meA ? ck : c0;
    bool myFocus = meA ? f0 : fk;
    if (isAlly(other)) dAlly = smin(dAlly, d, k);
    else if (myFocus) dFront = smin(dFront, d, k);
    else dOwn = smin(dOwn, d, k);
    if (meB && myFocus && !isAlly(c0)) dRipple = smin(dRipple, d, k);
  } else if (isAlly(c0) || isAlly(ck)) {
    dAlly = smin(dAlly, d, k);
  } else if (c0 > 0 && ck > 0) {
    dEE = smin(dEE, d, k);
  } else {
    dEnemy = smin(dEnemy, d, k);
  }
}

void main() {
  vec2 px = vec2(gl_FragCoord.x, uView.y - gl_FragCoord.y);
  vec2 w = (px - 0.5 * uView) / uCam.z + uCam.xy;
  vec2 sUv = gl_FragCoord.xy / uView;
  vec3 vis3 = texture(uVis, sUv).rgb;
  float vis = vis3.r;              // anything of ours sees here (crisp edge)
  float wide = vis3.g;             // radar-class coverage (+ halo outside it)
  float soft = vis3.b;             // coverage fading in over ~24 px inside the edge
  float fringe = wide * (1.0 - vis);
  // union outline of our coverage: the 0.5 contour of the crisp mask (forward differences:
  // screen derivatives come in 2×2 blocks and would break the line into dashes)
  // (own sensors only: allied coverage lights the fog but is not outlined)
  float visOwn = texture(uVis, sUv).a;
  vec2 vt = 1.0 / uView;
  vec2 vg = vec2(texture(uVis, sUv + vec2(vt.x, 0.0)).a, texture(uVis, sUv + vec2(0.0, vt.y)).a) - visOwn;
  float visEdge = 1.0 - smoothstep(0.35, 1.0, abs(visOwn - 0.5) / max(length(vg) * 1.2, 1e-3));
  vec2 dp = floor(gl_FragCoord.xy / max(1.0, floor(uDpr + 0.25)));   // dither space (CSS px)
  float bay = bayer4(dp);
  float pxPerMu = uCam.z;
  float grain = hash12(gl_FragCoord.xy);

  vec3 col = vec3(0.006, 0.016, 0.012) + grain * vec3(0.008, 0.012, 0.009);

  // ---- graticule (15°): dotted inside coverage, tiny crosses in the dark
  {
    const float COS30 = 0.8660254;
    float gx = mod(w.x - 4.0 * COS30, 15.0 * COS30);    // meridians: lon -165 + 15k  (x = (lon + 169)·cos30)
    float gy = mod(w.y - 3.0, 15.0);                    // parallels: lat 75 - 15k   (y = 78 - lat)
    float dx = min(gx, 15.0 * COS30 - gx) * pxPerMu;
    float dy = min(gy, 15.0 - gy) * pxPerMu;
    float on = 0.0;
    float dotted = mod(floor((px.x + px.y) / (2.0 * uDpr)), 2.0);
    if (dx < 0.6 * uDpr) on = max(on, dotted * 0.06 * vis);
    if (dy < 0.6 * uDpr) on = max(on, dotted * 0.06 * vis);
    if ((dx < 0.6 * uDpr && dy < 4.0 * uDpr) || (dy < 0.6 * uDpr && dx < 4.0 * uDpr)) on = max(on, 0.05);
    col += C_ME * on * 0.6;
  }

  if (w.x < 0.0 || w.y < 0.0 || w.x > uWorld.x || w.y > uWorld.y) { outColor = vec4(col * 0.5, 1.0); return; }

  vec2 lUv = w / uWorld;
  float landV = texture(uLand, lUv).r;
  float fw = max(fwidth(landV), 1e-4);
  float land = smoothstep(0.5 - fw, 0.5 + fw, landV);

  // ---- ocean: faint shelf glow where we can see
  {
    float shelf = textureLod(uLand, lUv, 3.0).r;
    vec3 ocean = C_ME * shelf * 0.045 * soft;
    ocean += C_ME * fringe * 0.035 * (bay < 0.5 ? 1.0 : 0.4);
    col += ocean * (1.0 - land);
  }

  if (land > 0.0) {
    vec2 ww = w + warpOffset(w);
    ivec2 ax = axialOf(ww);
    int h = hexAtAxial(ax);
    vec3 lc = vec3(0.0);
    if (h < 0) {
      lc = C_GREY * 0.012;
    } else {
      vec2 rel = ww - axialCenter(ax);
      float apothem = 0.8660254 * uLat.z;
      float K = ROUND * uLat.z;
      const vec2 N[6] = vec2[6](vec2(1.0, 0.0), vec2(0.5, -0.8660254), vec2(-0.5, -0.8660254), vec2(-1.0, 0.0), vec2(-0.5, 0.8660254), vec2(0.5, 0.8660254));
      const ivec2 D[6] = ivec2[6](ivec2(1, 0), ivec2(1, -1), ivec2(0, -1), ivec2(-1, 0), ivec2(-1, 1), ivec2(0, 1));
      uvec4 s = hexState(h);
      int cls0 = (s.a & 2u) != 0u ? int(s.r) : -1;
      int rc0 = roundClass(s);
      // ---------- soft corners: the smooth distance to the nearest other owner; a pixel past
      // it (in a rounded-off corner) belongs to that neighbour (CPU twin: renderHexAt)
      float nd[6];
      int nh[6];
      uvec4 ns[6];
      float sm = 1e9, dmin = 1e9;
      int kmin = -1;
      for (int k = 0; k < 6; k++) {
        float d = apothem - dot(rel, N[k]);
        nd[k] = d;
        nh[k] = -1;
        ns[k] = uvec4(0u);
        if (d > uLat.z) continue;                 // far from this edge
        int hk = hexAtAxial(ax + D[k]);
        if (hk < 0 || hk == h) continue;
        uvec4 sk = hexState(hk);
        nh[k] = hk;
        ns[k] = sk;
        if (roundClass(sk) == rc0) continue;
        sm = smin(sm, d, K);
        if (d < dmin) { dmin = d; kmin = k; }
      }
      bool flipped = sm < 0.0 && kmin >= 0;
      uvec4 sOld = s;
      if (flipped) {
        h = nh[kmin];
        s = ns[kmin];
      }
      int owner = int(s.r);
      float dens = float(s.g) / 15.0;
      float rad = float(s.b) / 15.0;
      bool live = (s.a & 1u) != 0u;
      bool seen = (s.a & 2u) != 0u;
      bool focus = (s.a & 4u) != 0u;
      float liveF = max(soft, live ? 0.8 : 0.0);

      // ---------- fill
      if (!seen) {
        lc = C_GREY * 0.01;
      } else if (owner == uMe) {
        float lit = 0.42 + 0.58 * wide;
        float I = 0.045 + 0.15 * pattern(7, dp);
        I *= lit * (0.75 + 0.45 * dens);
        lc = C_ME * I;
      } else if (owner == 0) {
        float I = (0.022 + 0.08 * pattern(9, dp)) * (0.8 + 0.2 * liveF);
        lc = C_GREY * I;
      } else {
        vec4 oc = uOwnerCol[owner];
        int pid = int(oc.a + 0.5);
        if (isAlly(owner)) {
          // allies: cooler teal, a different (dot) pattern and ~0.6× our fill
          float I = (0.03 + 0.16 * pattern(8, dp)) * (0.4 + 0.6 * soft);
          lc = oc.rgb * I;
        } else if (liveF > 0.01) {
          float I = (0.03 + 0.2 * pattern(pid, dp)) * liveF * (0.8 + 0.35 * dens);
          lc = oc.rgb * I;
        } else {
          // outside our sensors (borders are public): the nation's colour, a little desaturated and
          // about half as bright as land we watch — never faded to black
          vec3 far = mix(oc.rgb, C_GREY, 0.3);
          lc = far * (0.03 + 0.13 * pattern(pid, dp)) * (0.8 + 0.35 * dens);
        }
      }

      // ---------- fresh capture glow
      float age = uTime - hexFlip(h);
      if (seen && age >= 0.0 && age < 30.0) {
        float g = exp(-age / 7.0);
        vec3 gc = owner == uMe ? C_ME : (isAlly(owner) ? C_ALLY : uOwnerCol[owner].rgb);
        lc += gc * g * (0.08 + (bay < 0.35 * g ? 0.22 : 0.0)) * max(liveF, owner == uMe ? 1.0 : 0.0);
      }

      // ---------- radiation: ash + speckle
      if (rad > 0.0) {
        lc *= 1.0 - 0.65 * rad;
        float speck = hash12(floor(dp * 0.5) + floor(uTime * 3.0)) < 0.25 * rad ? 1.0 : 0.0;
        lc += C_GREY * rad * (0.03 + 0.12 * speck);
      }

      // ---------- borders (smooth edge distance to each differing neighbour)
      float dOwn = 1e9, dFront = 1e9, dAlly = 1e9, dEnemy = 1e9, dEE = 1e9, dRipple = 1e9;
      if (flipped) {
        // in a neighbour's rounded-off corner: the one border is the arc back to the hex we are in
        // (none on the edge of what we have seen — that edge is the fog itself)
        cls0 = int(s.r);
        if (seen && (sOld.a & 2u) != 0u && cls0 != int(sOld.r)) border(cls0, focus, int(sOld.r), (sOld.a & 4u) != 0u, -sm, K, dOwn, dFront, dAlly, dEnemy, dEE, dRipple);
      } else {
        for (int k = 0; k < 6; k++) {
          if (nh[k] < 0 || nd[k] > 0.6 * uLat.z + K) continue;
          uvec4 sk = ns[k];
          int clsK = (sk.a & 2u) != 0u ? int(sk.r) : -1;
          if (clsK == cls0 || clsK < 0 || cls0 < 0) continue;
          border(cls0, focus, clsK, (sk.a & 4u) != 0u, nd[k], K, dOwn, dFront, dAlly, dEnemy, dEE, dRipple);
        }
      }
      float pxd;
      // the push front: brightest edge on screen; its life says how the push goes
      // (advancing: bright, rolling; stalled: steady and dimmer; losing: amber flicker)
      if (dFront < 1e8) {
        pxd = dFront * pxPerMu / uDpr;
        float core = exp(-pxd * pxd * 0.55);
        float halo = exp(-pxd * 0.55) * 0.3;
        if (uFront == 3) {
          float flick = hash12(vec2(floor(uTime * 7.0), 3.0)) < 0.3 ? 0.45 : 1.0;
          lc += mix(C_WHITE, C_AMBER, 0.55) * core * 0.8 * flick + C_AMBER * halo * 0.8;
        } else if (uFront == 1) {
          lc += C_WHITE * core * 1.05 + C_ME * halo * 1.2;
        } else {
          lc += C_WHITE * core * 0.75 + C_ME * halo * 0.8;
        }
      }
      if (dOwn < 1e8) {
        pxd = dOwn * pxPerMu / uDpr;
        lc += C_ME * (exp(-pxd * pxd * 0.9) * 0.42 + exp(-pxd * 0.7) * 0.06) * (0.55 + 0.45 * vis);
      }
      if (dAlly < 1e8) {
        pxd = dAlly * pxPerMu / uDpr;
        lc += C_ALLY * exp(-pxd * pxd * 1.2) * 0.3 * (0.5 + 0.5 * vis);
      }
      if (dEE < 1e8) {
        pxd = dEE * pxPerMu / uDpr;
        float dash = mod(floor((px.x + px.y) / (3.0 * uDpr)), 2.0);
        vec3 ec = uOwnerCol[max(cls0, 1)].rgb;
        lc += ec * exp(-pxd * pxd * 1.4) * dash * (0.3 + 0.18 * liveF);
      }
      if (dEnemy < 1e8 && cls0 != 0) {
        pxd = dEnemy * pxPerMu / uDpr;
        lc += uOwnerCol[cls0].rgb * exp(-pxd * pxd * 1.6) * (0.26 + 0.06 * liveF);
      }
      // a nation the Tab board points at: its land lights up in its colour, its borders glow
      if (uHilite > 0 && owner == uHilite && seen) {
        vec4 hc = uOwnerCol[uHilite];
        vec3 hcol = uHiliteRed > 0.5 ? vec3(1.0, 0.24, 0.2) : isAlly(uHilite) ? C_ALLY : hc.rgb;
        lc += hcol * (0.07 + 0.32 * pattern(int(hc.a + 0.5), dp)) * uHiliteK;
        float dB = min(min(dEnemy, dEE), min(dAlly, dOwn));
        if (dB < 1e8) {
          pxd = dB * pxPerMu / uDpr;
          lc += hcol * (exp(-pxd * pxd * 0.25) * 1.1 + exp(-pxd * 0.3) * 0.25) * uHiliteK;
        }
      }
      // pressure echoes ahead of our focused front: rolling out (advancing), standing still
      // (stalled) or pulling back towards us in amber (losing)
      if (dRipple < 1e8) {
        pxd = dRipple * pxPerMu / uDpr;
        float roll = uFront == 1 ? mod(uTime * 13.0, 14.0) : uFront == 3 ? -mod(uTime * 6.0, 14.0) : 0.0;
        float ph = pxd - roll;
        float ring = abs(mod(ph, 14.0) - 7.0);
        float reach = uFront == 1 ? 52.0 : uFront == 3 ? 26.0 : 34.0;
        float fade = 1.0 - smoothstep(10.0, reach, pxd);
        float broken = mod(floor((px.x * 3.0 + px.y * 5.0) / (8.0 * uDpr)), 5.0) < 4.0 ? 1.0 : 0.0;
        float echo = uFront == 1 ? 0.5 : uFront == 3 ? 0.3 : 0.2;
        vec3 ec = uFront == 3 ? C_AMBER : C_ME;
        lc += ec * (ring > 5.8 ? echo : 0.0) * fade * broken;
        // land being absorbed: green dither spill (only while it is)
        if (uFront == 1) {
          float spill = 1.0 - smoothstep(0.0, 26.0, pxd);
          lc += C_ME * (bay < 0.35 * spill ? 0.22 * spill : 0.0);
        }
      }
    }
    col = mix(col, col * 0.4 + lc, land);
  }

  // ---- the dark: faint drifting static where nothing of ours looks
  {
    float dark = (1.0 - soft) * (1.0 - wide);
    float drift = 0.5 + 0.5 * sin(w.x * 0.21 + w.y * 0.13 + uTime * 0.5) * sin(w.y * 0.17 - uTime * 0.31);
    float speck = hash12(floor(dp * 0.5) + floor(uTime * 12.0) * vec2(13.0, 7.0)) < 0.035 * drift ? 1.0 : 0.0;
    col += C_GREY * dark * (speck * 0.05 + drift * 0.006);
  }

  col += C_ME * visEdge * 0.5;
  outColor = vec4(col, 1.0);
}`;

export class TerritoryLayer {
  private prog: Program;
  private vao: WebGLVertexArrayObject;
  private landTex: WebGLTexture;
  private cellTex: WebGLTexture;
  private stateTex: WebGLTexture;
  private flipTex: WebGLTexture;
  private warpTex: WebGLTexture;
  private readonly stateH: number;
  readonly cells: Int32Array;

  constructor(
    private gl: GL,
    private world: LoadedWorld,
    landMask: TexImageSource,
    private lookup: HexLookup,
  ) {
    this.prog = makeProgram(gl, 'territory', FULLSCREEN_VS, FS, [
      'uView',
      'uCam',
      'uCells',
      'uState',
      'uFlip',
      'uWarp',
      'uWarpSize',
      'uLat',
      'uCellDim',
      'uLand',
      'uVis',
      'uWorld',
      'uOwnerCol',
      'uMe',
      'uAllyMask',
      'uTime',
      'uDpr',
      'uFront',
      'uHilite',
      'uHiliteK',
      'uHiliteRed',
    ]);
    this.vao = makeFullscreenVao(gl, this.prog.prog);

    this.landTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.landTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, gl.RED, gl.UNSIGNED_BYTE, landMask);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const { lattice } = world.extras;
    this.cells = lookup.cells;
    this.cellTex = makeTexture(gl, lattice.cols, lattice.rows, gl.R32I, gl.RED_INTEGER, gl.INT, gl.NEAREST, this.cells);
    this.stateH = Math.ceil(world.landCount / STATE_W);
    this.stateTex = makeTexture(gl, STATE_W, this.stateH, gl.RGBA8UI, gl.RGBA_INTEGER, gl.UNSIGNED_BYTE, gl.NEAREST);
    this.flipTex = makeTexture(gl, STATE_W, this.stateH, gl.R32F, gl.RED, gl.FLOAT, gl.NEAREST);
    this.warpTex = makeTexture(gl, lookup.warp.w, lookup.warp.h, gl.RG8, gl.RG, gl.UNSIGNED_BYTE, gl.LINEAR, lookup.warp.data);
  }

  uploadState(state: Uint8Array): void {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.stateTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, STATE_W, this.stateH, gl.RGBA_INTEGER, gl.UNSIGNED_BYTE, state);
  }

  uploadFlip(flip: Float32Array): void {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.flipTex);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, STATE_W, this.stateH, gl.RED, gl.FLOAT, flip);
  }

  /** Bind the hex textures + lattice uniforms for any program using GLSL_HEX (4 texture units). */
  bindHexUniforms(p: Program, unitBase: number): void {
    const gl = this.gl;
    const { lattice } = this.world.extras;
    gl.activeTexture(gl.TEXTURE0 + unitBase);
    gl.bindTexture(gl.TEXTURE_2D, this.cellTex);
    gl.uniform1i(p.u.uCells!, unitBase);
    gl.activeTexture(gl.TEXTURE0 + unitBase + 1);
    gl.bindTexture(gl.TEXTURE_2D, this.stateTex);
    gl.uniform1i(p.u.uState!, unitBase + 1);
    gl.activeTexture(gl.TEXTURE0 + unitBase + 2);
    gl.bindTexture(gl.TEXTURE_2D, this.flipTex);
    gl.uniform1i(p.u.uFlip!, unitBase + 2);
    gl.activeTexture(gl.TEXTURE0 + unitBase + 3);
    gl.bindTexture(gl.TEXTURE_2D, this.warpTex);
    gl.uniform1i(p.u.uWarp!, unitBase + 3);
    gl.uniform2f(p.u.uWarpSize!, this.lookup.warp.w / WARP_RES, this.lookup.warp.h / WARP_RES);
    gl.uniform4f(p.u.uLat!, lattice.originX, lattice.originY, lattice.hexR, WARP_AMP);
    gl.uniform2i(p.u.uCellDim!, lattice.cols, lattice.rows);
  }

  draw(f: FrameParams, visTex: WebGLTexture): void {
    const gl = this.gl;
    const p = this.prog;
    gl.useProgram(p.prog);
    gl.uniform2f(p.u.uView!, f.viewW, f.viewH);
    gl.uniform3f(p.u.uCam!, f.camX, f.camY, f.zoomPx);
    this.bindHexUniforms(p, 0);
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, this.landTex);
    gl.uniform1i(p.u.uLand!, 4);
    gl.activeTexture(gl.TEXTURE5);
    gl.bindTexture(gl.TEXTURE_2D, visTex);
    gl.uniform1i(p.u.uVis!, 5);
    gl.uniform2f(p.u.uWorld!, this.world.width, this.world.height);
    gl.uniform4fv(p.u.uOwnerCol!, f.ownerColors);
    gl.uniform1i(p.u.uMe!, f.me);
    gl.uniform1i(p.u.uAllyMask!, f.allyMask);
    gl.uniform1f(p.u.uTime!, f.time);
    gl.uniform1f(p.u.uDpr!, f.dpr);
    gl.uniform1i(p.u.uFront!, f.front);
    gl.uniform1i(p.u.uHilite!, f.hilite);
    gl.uniform1f(p.u.uHiliteK!, f.hiliteK);
    gl.uniform1f(p.u.uHiliteRed!, f.hiliteRed);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.bindVertexArray(null);
  }
}
